// ---------------------------------------------------------------------------
// "A new version is ready."
//
// The whole reason this exists rather than the service worker just updating
// itself: a till that reloads on its own loses whatever is in the cart. A
// cashier halfway through weighing an order would watch it vanish and would
// never trust the screen again.
//
// So the new version installs quietly, waits, and this asks. Saying "later"
// dismisses it for the session; the update is still there the next time the
// browser is opened, which for a shop is the following morning.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';
import { create } from 'zustand';
import { RefreshCw } from 'lucide-react';
import { useI18n } from '../../i18n/I18nProvider';
import { applyUpdate } from '../../lib/serviceWorker';

interface UpdateState {
  available: boolean;
  markAvailable: () => void;
  dismiss: () => void;
}

/// A tiny store rather than context, so the service-worker registration - which
/// runs outside React - can flip it without a provider in between.
const useUpdateStore = create<UpdateState>((set) => ({
  available: false,
  markAvailable: () => set({ available: true }),
  dismiss: () => set({ available: false }),
}));

/// Handed to the service-worker registration so it can announce an update.
export function useUpdateAvailable(): () => void {
  const markAvailable = useUpdateStore((state) => state.markAvailable);
  return useCallback(() => markAvailable(), [markAvailable]);
}

export function UpdatePrompt() {
  const { t } = useI18n();
  const available = useUpdateStore((state) => state.available);
  const dismiss = useUpdateStore((state) => state.dismiss);
  const [applying, setApplying] = useState(false);

  if (!available) return null;

  return (
    <div
      className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-sm rounded-panel border border-line bg-surface p-4 shadow-lg sm:inset-x-auto sm:end-4"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-butcher-green" aria-hidden="true" />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">{t.offline.updateTitle}</p>
          <p className="mt-0.5 text-sm text-ink-muted">{t.offline.updateBody}</p>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={applying}
              onClick={() => {
                setApplying(true);
                applyUpdate();
              }}
            >
              {t.offline.updateNow}
            </button>
            <button type="button" className="btn-ghost text-xs" onClick={dismiss}>
              {t.offline.updateLater}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
